import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { client } from '../apolloClient';
import { TabCoordinatedSessionManager } from '../services/TabCoordinatedSessionManager';

/**
 * When this browser last logged out (any tab, any page load). Lives in
 * localStorage so a page restored from the back/forward cache - whose
 * in-memory auth state is frozen from before the logout - can tell that it
 * is stale.
 */
export const LOGGED_OUT_AT_KEY = 'pithplay_logged_out_at';

export const markLoggedOut = (at: number = Date.now()) => {
  try {
    localStorage.setItem(LOGGED_OUT_AT_KEY, String(at));
  } catch {
    // Storage blocked; the bfcache check falls back to "no current user".
  }
};

export const readLoggedOutAt = (): number => {
  try {
    const value = Number(localStorage.getItem(LOGGED_OUT_AT_KEY));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
};

/**
 * Whether a page just restored from the back/forward cache must reload.
 *
 * A bfcache restore brings the page back exactly as it was left - React
 * state, Firebase's in-memory user and all - without running any of the
 * route guards again. If the user logged out after this page last saw them
 * signed in (in another page load or tab), or there is no user any more,
 * the protected content on screen is stale: reload so auth is read afresh.
 * A page that never showed a signed-in user had nothing protected on it.
 */
export const shouldReloadRestoredPage = ({
  persisted,
  hasCurrentUser,
  lastSignedInSeenAt,
  loggedOutAt,
}: {
  persisted: boolean;
  hasCurrentUser: boolean;
  lastSignedInSeenAt: number;
  loggedOutAt: number;
}): boolean => {
  if (!persisted) return false;
  if (lastSignedInSeenAt <= 0) return false;
  if (!hasCurrentUser) return true;
  return loggedOutAt >= lastSignedInSeenAt;
};

/**
 * The one way the app signs a user out on purpose (Header, /logout, an
 * expired session). Besides signing out of Firebase it tells every other
 * open tab to stop refreshing and sign out too - a refresh in another tab
 * re-persisted the user, and Firebase then synced them back into this tab,
 * so Back and typed-in URLs reopened the dashboard after logout - and drops
 * every cached GraphQL result so the next user on this browser can't see
 * the previous one's projects or billing.
 *
 * Throws if Firebase could not sign out; the session is then left running.
 */
export const performLogout = async (): Promise<void> => {
  const sessionManager = TabCoordinatedSessionManager.current();
  sessionManager?.beginLogout();
  try {
    await signOut(auth);
  } catch (error) {
    sessionManager?.cancelLogout();
    throw error;
  }

  markLoggedOut();
  try {
    // Written by older builds of the sign-in page; nothing reads it.
    localStorage.removeItem('authToken');
  } catch {
    // Storage blocked - nothing to remove.
  }
  try {
    await client.clearStore();
  } catch (cacheError) {
    console.warn('Failed to clear Apollo cache on logout:', cacheError);
  }
};
